import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Card, Col, DatePicker, Empty, Form, Input,
  InputNumber, Modal, Row, Select, Space, Statistic, Table, Tag,
  Tooltip, Typography, message
} from 'antd';
import {
  ReloadOutlined, PlusOutlined, EyeOutlined, DeleteOutlined,
  LockOutlined, StopOutlined, BankOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

import { ErpMasterShell } from '../../../../components/common/erp-grid';
import useApprovalMfa from '../../../../hooks/useApprovalMfa';
import { bankReconciliationApi } from './bankReconciliationApi';
import {
  RECONCILIATION_STATUS_COLOR, RECONCILIATION_STATUS_LABEL,
  formatBRSAmount, findReconciliationFormErrors
} from './bankReconciliationHelpers';
import {
  friendlyAccountsErrorMessage, friendlyAccountsEmptyMessage
} from '../accountsPageState';
import '../accounts.css';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// ─────────────────────────────────────────────────────────────────
// Glass-i — Bank Reconciliations list page (Stage 6E.1).
//
// Lists all reconciliations with status / period / matched-count
// columns + a "New Reconciliation" modal. Detail / matching surface
// lives on the detail page at /erp/accounts/bank-reconciliations/:id.
// ─────────────────────────────────────────────────────────────────

const STATUS_OPTIONS = [
  { value: '',            label: 'All statuses'    },
  { value: 'draft',       label: 'Draft'           },
  { value: 'in_progress', label: 'In progress'     },
  { value: 'reconciled',  label: 'Reconciled'      },
  { value: 'locked',      label: 'Locked'          },
  { value: 'cancelled',   label: 'Cancelled'       }
];

function BankLedgerSelect({ value, onChange, disabled, placeholder }) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetch = useCallback(async (q) => {
    setLoading(true);
    try {
      const out = await bankReconciliationApi.cashBankLookup(q || '', 100);
      // Backend lookup may return cash+bank; filter to bank-side ledgers.
      const filtered = (out || []).filter((row) =>
        row.subtype === 'bank'
        || (row.subtype == null && /^bank/i.test(row.accountGroup || '')));
      setOptions(filtered);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(''); }, [fetch]);

  return (
    <Select
      size="small"
      showSearch
      filterOption={false}
      loading={loading}
      disabled={disabled}
      value={value}
      onChange={onChange}
      placeholder={placeholder || 'Select a bank ledger'}
      onSearch={(t) => fetch(t)}
      style={{ width: 320 }}
      options={options.map((o) => ({
        value: o.id, label: `${o.accountCode || ''} · ${o.accountName || ''}`.trim()
      }))} />
  );
}

function NewReconciliationModal({ open, onClose, onCreated }) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const save = useCallback(async () => {
    let values;
    try { values = await form.validateFields(); }
    catch (_e) { return; }
    const payload = {
      bankAccountId: values.bankAccountId,
      statementFromDate: values.range[0].format('YYYY-MM-DD'),
      statementToDate:   values.range[1].format('YYYY-MM-DD'),
      statementOpeningBalance: values.statementOpeningBalance ?? null,
      statementClosingBalance: values.statementClosingBalance ?? null,
      remarks: values.remarks || null
    };
    const errs = findReconciliationFormErrors({
      bankAccountId: payload.bankAccountId,
      statementFromDate: payload.statementFromDate,
      statementToDate:   payload.statementToDate
    });
    if (errs.length) { message.error(errs[0][1]); return; }
    setSaving(true);
    try {
      const created = await bankReconciliationApi.create(payload);
      message.success('Bank reconciliation created.');
      form.resetFields();
      onCreated && onCreated(created);
    } catch (e) {
      message.error(friendlyAccountsErrorMessage(e, 'bank_reconciliation'));
    } finally { setSaving(false); }
  }, [form, onCreated]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={save}
      okText="Create"
      confirmLoading={saving}
      title="New Bank Reconciliation"
      width={620}
      destroyOnClose>
      <Form form={form} layout="vertical" preserve={false}>
        <Form.Item label="Bank account" name="bankAccountId"
                   rules={[{ required: true, message: 'Pick a bank ledger.' }]}>
          <BankLedgerSelect />
        </Form.Item>
        <Form.Item label="Statement period" name="range"
                   rules={[{ required: true, message: 'Pick a statement period.' }]}>
          <RangePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
        </Form.Item>
        <Row gutter={12}>
          <Col xs={24} md={12}>
            <Form.Item label="Statement opening balance (₹)"
                       name="statementOpeningBalance">
              <InputNumber style={{ width: '100%' }} step={0.01} precision={2} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="Statement closing balance (₹)"
                       name="statementClosingBalance">
              <InputNumber style={{ width: '100%' }} step={0.01} precision={2} />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item label="Remarks" name="remarks">
          <Input.TextArea rows={2} maxLength={2000} showCount />
        </Form.Item>
      </Form>
    </Modal>
  );
}

export default function BankReconciliationsPage() {
  const navigate = useNavigate();
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [bankAccountId, setBankAccountId] = useState(null);
  const [status, setStatus]   = useState('');
  const [modalOpen, setModalOpen] = useState(false);

  const { runWithApproval, ApprovalMfaModalSlot } = useApprovalMfa();

  const fetchAll = useCallback(async () => {
    setLoading(true); setLoadError(null);
    try {
      const params = {};
      if (bankAccountId) params.bankAccountId = bankAccountId;
      if (status) params.status = status;
      const out = await bankReconciliationApi.list(params);
      setRows(out?.rows || []);
    } catch (e) {
      setLoadError(e);
      message.error(friendlyAccountsErrorMessage(e, 'bank_reconciliation'));
    } finally { setLoading(false); }
  }, [bankAccountId, status]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const onCreated = useCallback((created) => {
    setModalOpen(false);
    if (created && created.id) navigate(`/erp/accounts/bank-reconciliations/${created.id}`);
    else fetchAll();
  }, [navigate, fetchAll]);

  const onRemove = useCallback(async (rec) => {
    Modal.confirm({
      title: `Delete reconciliation for ${rec.bankAccountName || 'this bank account'}?`,
      content: 'You can delete a draft or in-progress reconciliation. Locked or finalised records cannot be deleted.',
      okText: 'Delete', okButtonProps: { danger: true },
      onOk: () => runWithApproval({
        operationKey: 'accounts.bank_reconciliation.delete',
        resourceType: 'bank_reconciliation',
        resourceId:   rec.id,
        run: ({ challengeId }) =>
          bankReconciliationApi.remove(rec.id, { approvalMfaChallengeId: challengeId })
      })
      .then(() => { message.success('Reconciliation deleted.'); return fetchAll(); })
      .catch((e) => message.error(friendlyAccountsErrorMessage(e, 'bank_reconciliation')))
    });
  }, [fetchAll, runWithApproval]);

  const onLock = useCallback(async (rec) => {
    Modal.confirm({
      title: `Lock reconciliation for ${rec.bankAccountName || 'this bank account'}?`,
      content: 'Locking freezes the reconciliation. Edits, matches and statement-line changes are blocked until you unlock.',
      okText: 'Lock',
      onOk: () => runWithApproval({
        operationKey: 'accounts.bank_reconciliation.lock',
        resourceType: 'bank_reconciliation',
        resourceId:   rec.id,
        run: ({ challengeId }) =>
          bankReconciliationApi.lock(rec.id, { approvalMfaChallengeId: challengeId })
      })
      .then(() => { message.success('Reconciliation locked.'); return fetchAll(); })
      .catch((e) => message.error(friendlyAccountsErrorMessage(e, 'bank_reconciliation')))
    });
  }, [fetchAll, runWithApproval]);

  const onCancel = useCallback(async (rec) => {
    Modal.confirm({
      title: `Cancel reconciliation for ${rec.bankAccountName || 'this bank account'}?`,
      content: 'Cancelling marks the record as cancelled. Matches remain but the record is no longer editable.',
      okText: 'Cancel reconciliation',
      okButtonProps: { danger: true },
      onOk: () => runWithApproval({
        operationKey: 'accounts.bank_reconciliation.cancel',
        resourceType: 'bank_reconciliation',
        resourceId:   rec.id,
        run: ({ challengeId }) =>
          bankReconciliationApi.cancel(rec.id, { approvalMfaChallengeId: challengeId })
      })
      .then(() => { message.success('Reconciliation cancelled.'); return fetchAll(); })
      .catch((e) => message.error(friendlyAccountsErrorMessage(e, 'bank_reconciliation')))
    });
  }, [fetchAll, runWithApproval]);

  const columns = useMemo(() => ([
    { title: 'Bank Account', dataIndex: 'bankAccountName', key: 'bankAccountName',
      render: (v, r) => (
        <span>
          <BankOutlined style={{ marginRight: 6, color: '#0369a1' }} />
          <Text strong>{v || '—'}</Text>
          {r.bankAccountCode ? (
            <Text type="secondary" style={{ marginLeft: 6, fontSize: 11 }}>
              {r.bankAccountCode}
            </Text>
          ) : null}
        </span>
      )},
    { title: 'Statement Period', key: 'period', width: 200,
      render: (_, r) => `${r.statementFromDate} → ${r.statementToDate}` },
    { title: 'Statement Closing', dataIndex: 'statementClosingBalance', key: 'sc',
      align: 'right', width: 150,
      render: (v) => v == null ? <Text type="secondary">—</Text>
                               : `₹ ${formatBRSAmount(v)}` },
    { title: 'Book Closing', dataIndex: 'bookClosingBalance', key: 'bc',
      align: 'right', width: 150,
      render: (v) => v == null ? <Text type="secondary">—</Text>
                               : `₹ ${formatBRSAmount(v)}` },
    { title: 'Difference', dataIndex: 'differenceAmount', key: 'diff',
      align: 'right', width: 140,
      render: (v) => {
        const n = Number(v) || 0;
        return (
          <Text strong style={{ color: Math.abs(n) < 0.01 ? '#1f7a25' : '#b91c1c' }}>
            ₹ {formatBRSAmount(n)}
          </Text>
        );
      }},
    { title: 'Matched / Unmatched', key: 'counts', width: 160, align: 'center',
      render: (_, r) => (
        <span>
          <Tag color="green">{r.matchedLineCount ?? 0}</Tag>
          <Tag>{r.unmatchedLineCount ?? 0}</Tag>
        </span>
      )},
    { title: 'Status', dataIndex: 'status', key: 'status', width: 110,
      render: (v) => (
        <Tag color={RECONCILIATION_STATUS_COLOR[v] || 'default'}>
          {RECONCILIATION_STATUS_LABEL[v] || v}
        </Tag>
      )},
    { title: 'Actions', key: 'actions', width: 200, align: 'right',
      render: (_, r) => (
        <Space size={4}>
          <Tooltip title="Open"><Button size="small" icon={<EyeOutlined />}
            onClick={() => navigate(`/erp/accounts/bank-reconciliations/${r.id}`)} /></Tooltip>
          <Tooltip title="Lock"><Button size="small" icon={<LockOutlined />}
            disabled={r.status === 'locked' || r.status === 'cancelled'}
            onClick={() => onLock(r)} /></Tooltip>
          <Tooltip title="Cancel"><Button size="small" icon={<StopOutlined />}
            danger
            disabled={r.status === 'cancelled' || r.status === 'locked'}
            onClick={() => onCancel(r)} /></Tooltip>
          <Tooltip title="Delete"><Button size="small" icon={<DeleteOutlined />}
            danger
            disabled={r.status === 'locked' || r.status === 'reconciled'}
            onClick={() => onRemove(r)} /></Tooltip>
        </Space>
      )}
  ]), [navigate, onLock, onCancel, onRemove]);

  return (
    <ErpMasterShell>
      <div className="erp-accounts-page">
        <div style={{ display: 'flex', justifyContent: 'space-between',
                      alignItems: 'center', marginBottom: 4 }}>
          <Title level={4} style={{ margin: 0 }}>Bank Reconciliation</Title>
          <Space>
            <Button size="small" icon={<ReloadOutlined />}
              onClick={fetchAll} loading={loading}>Refresh</Button>
            <Button size="small" type="primary" icon={<PlusOutlined />}
              onClick={() => setModalOpen(true)}>
              New Reconciliation
            </Button>
          </Space>
        </div>
        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>
          Match bank statement entries with book entries and track monthly reconciliation.
        </div>

        {loadError ? (
          <Alert type="error" showIcon style={{ marginBottom: 10 }}
            message="Could not load bank reconciliations"
            description={friendlyAccountsErrorMessage(loadError, 'bank_reconciliation')}
            action={<Button size="small" icon={<ReloadOutlined />}
              onClick={fetchAll}>Retry</Button>} />
        ) : null}

        <Card size="small" className="erp-accounts-filter" style={{ marginBottom: 10 }}>
          <Space wrap>
            <Space>
              <Text>Bank:</Text>
              <BankLedgerSelect value={bankAccountId}
                onChange={setBankAccountId} placeholder="All banks" />
            </Space>
            <Space>
              <Text>Status:</Text>
              <Select size="small" value={status} onChange={setStatus}
                style={{ width: 150 }} options={STATUS_OPTIONS} />
            </Space>
          </Space>
        </Card>

        <Card size="small">
          <Table
            size="small"
            rowKey="id"
            loading={loading}
            columns={columns}
            dataSource={rows}
            pagination={{ pageSize: 25, size: 'small' }}
            locale={{ emptyText:
              <Empty description={loading ? 'Loading…'
                : friendlyAccountsEmptyMessage('bank_reconciliation')} /> }}
          />
        </Card>

        <NewReconciliationModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          onCreated={onCreated} />

        {ApprovalMfaModalSlot}
      </div>
    </ErpMasterShell>
  );
}
